import { User, useAuth0 } from '@auth0/auth0-react'
import { isPubstackUserRole, scopeAllFeatures, UserRole, UserViewMode } from '@pubstack/common/src/rolesAndFeatures'
import { AlertEmail, UserPreferences } from '@pubstack/common/src/user'
/* user.hooks */
import PbstckUser from './User'

type AugmentedUser = User & {
  'https://pubstack.io/user_metadata': {
    currentScopeId: string
    alertEmail: AlertEmail
    preferences?: UserPreferences
  }
  'https://pubstack.io/app_metadata': {
    roles: UserRole
  }
}

export const useUser = (): PbstckUser | null => {
  const { user } = useAuth0<AugmentedUser>()
  const buildProfile = (user: AugmentedUser): PbstckUser => {
    const namespace = 'https://pubstack.io'
    let { roles: userRoles } = user[`${namespace}/app_metadata`] || { roles: [] }
    const { alertEmail, preferences } = user[`${namespace}/user_metadata`] || {}
    const currentScopeId = user[`${namespace}/user_metadata`].currentScopeId

    let view: UserViewMode | undefined
    if (isPubstackUserRole(userRoles) && userRoles.view) {
      view = userRoles.view
      userRoles = { [currentScopeId]: userRoles.view }
    }

    // pubstack features are auth0 permissions
    // they look like this: 'currentScopeId:feature:adx'
    const features: string[] = isPubstackUserRole(userRoles) ? scopeAllFeatures(currentScopeId) : user[`${namespace}/user_authorization`].permissions || []
    return new PbstckUser({
      profile: user,
      accessToken: user.accessToken,
      idToken: user.idToken,
      refreshToken: user.refreshToken,
      userRoles,
      currentScopeId,
      features,
      alertEmail,
      preferences,
      view,
    })
  }

  return user ? buildProfile(user) : null
}

/**
 * Hook for components inside protected routes where user is guaranteed to be authenticated.
 * Throws an error if user is null (should never happen in protected context).
 */
export const useAuthenticatedUser = (): PbstckUser => {
  const user = useUser()
  if (!user) {
    throw new Error('useAuthenticatedUser must be used within an authenticated context')
  }
  return user
}
